import { Navbar, Footer, useReveal } from '../components/Balsuite';

const problems = [
  { emoji: '🗂️', label: 'Project tracker', tool: 'Jira / Asana' },
  { emoji: '⏱️', label: 'Timesheets', tool: 'Toggl / Excel' },
  { emoji: '🏖️', label: 'Leave requests', tool: 'Email / Forms' },
  { emoji: '📦', label: 'Asset tracking', tool: 'Spreadsheet' },
  { emoji: '👤', label: 'HR & onboarding', tool: 'Another HR tool' },
  { emoji: '🔥', label: 'Sprint boards', tool: 'Yet another app' },
  { emoji: '📊', label: 'Resource planning', tool: 'Excel / Float' },
  { emoji: '🔔', label: 'Team announcements', tool: 'Email / Slack' },
  { emoji: '📋', label: 'Audit & compliance', tool: 'Manual reports' },
];

const advantages = [
  {
    icon: '🔗',
    title: 'Everything connected',
    desc: 'Timesheets pull from tasks. Leave syncs with projects. Sprints reflect real capacity. No copy-pasting between tools — data flows automatically.',
  },
  {
    icon: '⚡',
    title: 'One login, zero context switching',
    desc: 'Your entire team — dev, HR, ops, finance — works from a single dashboard. No tab overload, no "which app was that in?" moments.',
  },
  {
    icon: '📊',
    title: 'Real insights, not data silos',
    desc: 'Because all your data lives in one place, reports are actually meaningful. See utilisation, velocity, leave impact, and delivery risk in one view.',
  },
  {
    icon: '🛡️',
    title: 'Enterprise-grade, startup-friendly',
    desc: 'Role-based permissions, audit trails, GDPR compliance, and asset lifecycle management — without the enterprise price tag or 6-month onboarding.',
  },
  {
    icon: '🚀',
    title: 'Up in an afternoon',
    desc: 'No IT team required. Invite your team, set up departments, and start tracking work in under 10 minutes. Seriously.',
  },
  {
    icon: '🤝',
    title: 'Built for every role',
    desc: 'Admins see the big picture, managers track their teams, employees log their work. Custom dashboards for every role, out of the box.',
  },
];

const comparisons = [
  { feature: 'Project & task management', balsuite: true, others: true },
  { feature: 'Unified Sprint & backlog management', balsuite: true, others: true },
  { feature: 'Task Management & Utilization', balsuite: true, others: false },
  { feature: 'Timesheet tracking', balsuite: true, others: false },
  { feature: 'Leave management', balsuite: true, others: false },
  { feature: 'Asset lifecycle tracking', balsuite: true, others: false },
  { feature: 'Employee onboarding', balsuite: true, others: false },
  { feature: 'Resource utilisation view', balsuite: true, others: false },
  { feature: 'Cross-module reporting', balsuite: true, others: false },
  { feature: 'Role-based dashboards', balsuite: true, others: false },
  { feature: 'Hodiday Calender', balsuite: true, others: false },
  { feature: 'Existing Tool Integration', balsuite: true, others: false },
  { feature: 'Announcements & Alerts', balsuite: true, others: false },
  { feature: 'Single subscription', balsuite: true, others: false },
];

const stats = [
  { val: '5+', label: 'Tools replaced', color: '#6366f1' },
  { val: '40%', label: 'Faster project delivery', color: '#8b5cf6' },
  { val: '10x', label: 'Less admin overhead', color: '#0891b2' },
  { val: '99.9%', label: 'Uptime SLA', color: '#059669' },
];

export default function WhyBalsuitePage() {
  useReveal();

  return (
    <div className="balsuite-page">
      <Navbar />

      {/* ── HERO ── */}
      <section style={{ padding: '2rem 2rem 3rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)',
          width: '700px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(99,102,241,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', maxWidth: '760px', margin: '0 auto' }}>
          <span className="sec-label reveal" style={{ display: 'inline-block' }}>Why Balsuite</span>
          <h1 className="sec-h2 reveal delay-1" style={{ marginTop: '0.75rem', marginBottom: '1.25rem' }}>
            Stop juggling tools.<br />
            <span className="grad-text">Start shipping work.</span>
          </h1>
          <p className="sec-sub reveal delay-2" style={{ maxWidth: '540px', margin: '0 auto 2.5rem' }}>
            Most companies run on 5–8 disconnected tools. Balsuite replaces all of them with one
            unified workspace — so your team spends time working, not switching.
          </p>
          <div className="reveal delay-3" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/contact"><button className="btn-gradient btn-lg">Get started free →</button></a>
            <a href="/pricing"><button className="btn-glass" style={{ padding: '0.9rem 2rem', fontSize: '1rem' }}>See pricing</button></a>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section style={{ padding: '4rem 2rem', background: 'var(--bg2)' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="sec-label reveal" style={{
              display: 'inline-block',
              background: 'rgba(244,63,94,0.08)', color: '#f43f5e', borderColor: 'rgba(244,63,94,0.2)',
            }}>The problem</span>
            <h2 className="sec-h2 reveal delay-1" style={{ marginTop: '0.75rem' }}>
              Your team uses <span style={{ color: '#f43f5e' }}>too many tools</span>
            </h2>
            <p className="sec-sub reveal delay-2" style={{ maxWidth: '480px', margin: '0.75rem auto 0' }}>
              Every new tool adds friction — another login, another import, another place where work gets lost.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
            {problems.map((p, i) => (
              <div key={p.label}
                className={`reveal scale-up delay-${i + 1}`}
                style={{
                  padding: '1.25rem', background: '#fff', borderRadius: '14px',
                  border: '1px solid rgba(244,63,94,0.12)',
                  textAlign: 'center',
                }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{p.emoji}</div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--t1)', marginBottom: '0.25rem' }}>{p.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--t3)' }}>{p.tool}</div>
              </div>
            ))}
          </div>

          <div className="reveal scale-up" style={{
            padding: '1.5rem 2rem', background: '#fff',
            border: '1px solid rgba(244,63,94,0.15)', borderRadius: '14px',
            display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '1.5rem' }}>🔁</span>
            <p style={{ color: 'var(--t2)', fontSize: '0.95rem', lineHeight: '1.6', flex: 1 }}>
              The average knowledge worker switches between <strong style={{ color: 'var(--t1)' }}>9 apps per day</strong> and loses{' '}
              <strong style={{ color: 'var(--t1)' }}>32 minutes</strong> to context switching alone. That's 2.5 hours a week — per person.
            </p>
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION ── */}
      <section style={{ padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span className="sec-label reveal" style={{ display: 'inline-block' }}>The Balsuite difference</span>
            <h2 className="sec-h2 reveal delay-1" style={{ marginTop: '0.75rem' }}>
              Six reasons teams <span className="grad-text">choose Balsuite</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {advantages.map((a, i) => (
              <div key={a.title}
                className={`reveal scale-up delay-${(i % 6) + 1}`}
                style={{
                  padding: '2rem', borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.07)',
                  background: i % 2 === 0
                    ? 'linear-gradient(135deg, rgba(99,102,241,0.03), rgba(139,92,246,0.03))'
                    : '#fff',
                }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{a.icon}</div>
                <h3 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontWeight: 700, fontSize: '1rem', color: 'var(--t1)', marginBottom: '0.6rem' }}>
                  {a.title}
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--t2)', lineHeight: '1.7' }}>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: '4rem 2rem', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2rem', textAlign: 'center' }}>
          {stats.map((s, i) => (
            <div key={s.label} className={`reveal delay-${i + 1}`}>
              <div style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: '3rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {s.val}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', marginTop: '0.5rem' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section style={{ padding: '5rem 2rem', background: 'var(--bg2)' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="sec-label reveal" style={{ display: 'inline-block' }}>Comparison</span>
            <h2 className="sec-h2 reveal delay-1" style={{ marginTop: '0.75rem' }}>
              Balsuite vs <span className="grad-text">point solutions</span>
            </h2>
            <p className="sec-sub reveal delay-2" style={{ maxWidth: '440px', margin: '0.75rem auto 0' }}>
              Other tools do one thing. Balsuite does everything — and connects them.
            </p>
          </div>

          <div className="reveal scale-up" style={{ background: '#fff', borderRadius: '18px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px', background: 'var(--bg3)', padding: '1rem 1.5rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Feature</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#6366f1', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Balsuite</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--t3)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Other tools</div>
            </div>
            {comparisons.map((c, i) => (
              <div key={c.feature} style={{
                display: 'grid', gridTemplateColumns: '1fr 140px 140px',
                padding: '0.875rem 1.5rem',
                background: i % 2 === 0 ? '#fff' : 'rgba(248,249,252,0.6)',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--t2)' }}>{c.feature}</span>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: c.balsuite ? 'rgba(99,102,241,0.1)' : 'rgba(244,63,94,0.1)',
                    color: c.balsuite ? '#6366f1' : '#f43f5e',
                    fontSize: '0.8rem', fontWeight: 700,
                  }}>
                    {c.balsuite ? '✓' : '✗'}
                  </span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: c.others ? 'rgba(99,102,241,0.1)' : 'rgba(244,63,94,0.1)',
                    color: c.others ? '#6366f1' : '#f43f5e',
                    fontSize: '0.8rem', fontWeight: 700,
                  }}>
                    {c.others ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '5rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 className="sec-h2 reveal" style={{ marginBottom: '1rem' }}>
            Ready to simplify<br />
            <span className="grad-text">how your team works?</span>
          </h2>
          <p className="sec-sub reveal delay-1" style={{ maxWidth: '420px', margin: '0 auto 2.5rem' }}>
            Join teams already using Balsuite to cut the chaos and ship faster.
            Free to start, no credit card required.
          </p>
          <div className="reveal delay-2" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/contact"><button className="btn-gradient btn-lg">Start for free →</button></a>
            <a href="/contact"><button className="btn-glass-cta">Talk to sales</button></a>
          </div>
          <p className="reveal delay-3" style={{ marginTop: '1.5rem', fontSize: '0.82rem', color: 'var(--t3)' }}>
            Free plan available · No credit card · Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
